import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Loader2, Navigation } from "lucide-react";
import FloorMapSvg from "@/components/FloorMapSvg";

/**
 * "Floor" tab content for the dining session:
 *  - Renders the floor map with the user's assigned table highlighted.
 *  - Shows table info + legend.
 *  - When still booked, shows arrival actions (auto + manual check-in).
 */
export default function FloorTabContent({
    reservation,
    tables,
    isBooked,
    isActive,
    checkingIn,
    onCheckIn,
}) {
    return (
        <div className="grid lg:grid-cols-[1fr_320px] gap-4">
            <FloorMapSvg tables={tables} assignedTableId={reservation.table_id} mode="customer" />
            <div className="space-y-3">
                <TableInfoCard reservation={reservation} isActive={isActive} />
                <LegendCard />
                {isBooked && <ArrivalActionsCard checkingIn={checkingIn} onCheckIn={onCheckIn} />}
            </div>
        </div>
    );
}

function TableInfoCard({ reservation, isActive }) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Your table</div>
                {reservation.table ? (
                    <>
                        <div className="font-display text-3xl mt-1">{reservation.table.label}</div>
                        <div className="text-sm text-muted-foreground">Seats {reservation.table.capacity}</div>
                        <p className="text-xs text-muted-foreground mt-3">
                            Look for the highlighted table on the map. Your server will be with you shortly.
                        </p>
                    </>
                ) : (
                    <>
                        <div className="font-display text-2xl mt-1">Not yet assigned</div>
                        <p className="text-xs text-muted-foreground mt-2">
                            {isActive ? "Awaiting host assignment." : "Your table will appear here after check-in."}
                        </p>
                    </>
                )}
            </CardContent>
        </Card>
    );
}

function LegendCard() {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground">Legend</div>
                <div className="mt-2 grid gap-1.5 text-xs">
                    <LegendItem color="hsl(var(--secondary))" label="Available" />
                    <LegendItem color="hsl(var(--muted))" label="Occupied" />
                    <LegendItem
                        color="hsl(var(--brand-terracotta) / 0.25)"
                        border="hsl(var(--brand-terracotta))"
                        label="Your table"
                    />
                </div>
            </CardContent>
        </Card>
    );
}

function ArrivalActionsCard({ checkingIn, onCheckIn }) {
    return (
        <Card>
            <CardContent className="p-4">
                <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-1">Arrival</div>
                <p className="text-sm">Already at the restaurant?</p>
                <Button
                    className="w-full mt-3"
                    onClick={() => onCheckIn({ manual: false })}
                    disabled={checkingIn}
                    data-testid="auto-check-in-button"
                >
                    {checkingIn ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Navigation className="h-4 w-4 mr-2" />}
                    Check in (use location)
                </Button>
                <Button
                    variant="secondary"
                    className="w-full mt-2"
                    onClick={() => onCheckIn({ manual: true })}
                    disabled={checkingIn}
                    data-testid="manual-check-in-button"
                >
                    I've arrived (manual)
                </Button>
            </CardContent>
        </Card>
    );
}

function LegendItem({ color, border, label }) {
    return (
        <div className="flex items-center gap-2">
            <span
                className="inline-block h-3 w-3 rounded"
                style={{ background: color, border: border ? `1.5px solid ${border}` : undefined }}
            />
            {label}
        </div>
    );
}
